import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { convertUtc } from "../../../../utils/dateUtils";
import { useClaimProfessionalTypeSelect } from "../../../admin/Dictionaries/hooks/getClaimProfessionalTypeSelectByClient";
import { useFunctionalAreaSelect } from "../../../admin/Dictionaries/hooks/getFunctionalAreasByClient";
import { useServiceIndicatorSelect } from "../../../admin/Dictionaries/hooks/getServiceIndicatorSelectsByClient";
import { ClaimPersonAffectedDto, ClaimProfesionalDto, ClaimFormDto, ClaimOutletContext } from "../../types";
import { ClaimShipmentStatusEnum } from "../../types/ClaimShipmentStatusEnum";
import { ClaimPersonTypeEnum, TimelineStatusEnum } from "../../types/enums";
import { useUsersSelect } from "../../../../hooks/getUsersByClient";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useClaimDeleteReasonSelect } from "../../../admin/Dictionaries/hooks/getClaimDeleteReasonByClient";
import { useDemandInFormatSelect } from "../../../admin/Dictionaries/hooks/getDemandInFormatByClient";
import { useDemandOutFormatSelect } from "../../../admin/Dictionaries/hooks/getDemandOutFormatByClient";
import { useDemandDerivationSelect } from "../../../admin/Dictionaries/hooks/getDemandDerivationByClient";
import notesMedicalIcon from "@/assets/notes-medical-thin-full.svg";
import {  getParticipantDataFromSoap } from "../../api/ClaimAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useClaimPersonalRelationshipSelect } from "../../../admin/Dictionaries/hooks/getPersonalRelationshipByClient";
import './../../pages/ClaimStyles.css'
import { getDetailsTimelineBackgroundColor } from "../../utils/claimStatusStyle";
import { ClaimStatusEnum } from "../../types/ClaimStatusEnum";
import { COLOR } from "../../../../config/constraits";
import { useRequestTypesSelect } from "../../../admin/Dictionaries/hooks/useRequestTypesSelect";
import { useClaimOriginsSelect } from "../../../admin/Dictionaries/hooks/useClaimOriginsSelect";
import ClaimProfessionalsSection from "../common/ClaimProfessionalsSection";
import { useOrganizationalStructuresSelectByUser } from "../../../admin/Dictionaries/hooks/useOrganizationalStructuresSelect";
import dangerTriangleIcon from "@/assets/triangle-exclamation-solid-full.svg";
import successTriangleIcon from "@/assets/triangle-exclamation-green-solid-full.png";
import ClaimsForPersonModal from "../modals/ClaimsForPersonModal";
import { SelectedFilter } from "../../../../components/Filters/types";
import { useGetClaimsForClaimant } from "../../hooks/useGetClaimsForClaimant";
import { useGetClaimsForAffectedPerson } from "../../hooks/useGetClaimsForAffectedPerson";
import { FilterTableKey, setSelectedFilters } from "../../../../redux/filtersSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../redux/store";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";

type claimSection = "person" | "affectedPerson" | "reason" | "professionals" | "internal" | "closing";

const RELATED_PEOPLE_DEBOUNCE_MS = 400;

export default function EditClaimData() {
    const { t } = useTranslation('claim');
    const {
        claimUpdate,
        setClaimUpdate,
        setMessageError,
        errors
    } = useOutletContext<ClaimOutletContext>();

    const { data: users } = useUsersSelect({});

    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');
    const isViewMode = mode == 'view';
    
    const dispatch = useDispatch<AppDispatch>();

    const [readOnly, setReadOnly] = useState<boolean>(false);
    // TODO: Obtener todos los selects en una misma llamada al back
    const { data: serviceIndicators } = useServiceIndicatorSelect({});
    const { data: functionalAreas } = useFunctionalAreaSelect({});
    const { data: claimProfessionalTypes } = useClaimProfessionalTypeSelect({});
    const { data: claimDeleteReasons } = useClaimDeleteReasonSelect({});
    const { data: claimInOutFormats } = useDemandInFormatSelect({});
    const { data: claimOutFormats } = useDemandOutFormatSelect({});
    const { data: demandDerivations } = useDemandDerivationSelect({});
    const { data: claimPersonalRelationships } = useClaimPersonalRelationshipSelect({});
    const { data: claimRequestTypes } = useRequestTypesSelect({});
    const { data: claimOrigins } = useClaimOriginsSelect({});
    const { data: organizationalStructures } = useOrganizationalStructuresSelectByUser({});

    const [showModal, setShowModal] = useState(false);
    const handleOpen = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    const [showModalCopyData, setShowModalCopyData] = useState(false);
    const [showClaimsForAffectedPersonModal, setShowClaimsForAffectedPersonModal] = useState(false);
    const [showClaimsForClaimantModal, setShowClaimsForClaimantModal] = useState(false);
    const handleOpenCopyData = () => setShowModalCopyData(true);
    const handleCloseCopyData = () => setShowModalCopyData(false);
    const [isSearchingAffectedPerson, setIsSearchingAffectedPerson] = useState(false);
    const [hasAffectedPersonSearchError, setHasAffectedPersonSearchError] = useState(false);

    const [status, setStatus] = useState<number>();

    const currentStatus = useRef<number>(claimUpdate?.status ?? 0);
    const [isMandatory, setIsMandatory] = useState(false);

    const { data: claimsForClaimant} = useGetClaimsForClaimant();
    const { data: claimsForAffectedPerson } = useGetClaimsForAffectedPerson();

    
    const [collapsedSections, setCollapsedSections] = useState<Set<claimSection>>(new Set());

    const hasAccessToClinicalHistory = useSelector((state: RootState) => state.currentUserInfo.data.hasAccessToClinicalHistory);

    useEffect(() => {
        if (!claimUpdate)
            return;

        const timeout = setTimeout(() => {
            searchRelatedPeople(ClaimPersonTypeEnum.Claimant);
        }, RELATED_PEOPLE_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [
        claimUpdate.nameAndSurname,
        claimUpdate.cip,
        claimUpdate.dni
    ]);

    useEffect(() => {
        if (!claimUpdate)
            return;

        const timeout = setTimeout(() => {
            searchRelatedPeople(ClaimPersonTypeEnum.Affected);
        }, RELATED_PEOPLE_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [
        claimUpdate.personAffected?.nameAndSurname,
        claimUpdate.personAffected?.cip,
        claimUpdate.personAffected?.dni,
        claimUpdate.personAffected?.medicalHistoryNumber
    ]);

    const toggleSection = (section: claimSection) => {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            next.has(section) ? next.delete(section) : next.add(section);
            return next;
        });
    };

    const isCollapsed = (section: claimSection) => collapsedSections.has(section);

    useEffect(() => {
        if (!claimUpdate)
            return;
        setReadOnly(claimUpdate.status === ClaimStatusEnum.Closed || isViewMode);
        if (organizationalStructures?.length == 1) {
            handleChange("organizationalStructureId", organizationalStructures[0].id)
        }
    }, [organizationalStructures, claimUpdate.status ]);

    useEffect(() => {
        if (isViewMode)
            return;

        setReadOnly(claimUpdate.status === ClaimStatusEnum.Closed);
    }, [isViewMode])


    if (!claimUpdate) {
        return (
            <div className="overlay-spinner">
                <Spinner animation="border" variant="light" />
            </div>
        );
    }

    const handleChange = (field: keyof ClaimFormDto, value: any) => {
        setClaimUpdate(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handlePersonAffectedChange = (
        field: keyof ClaimPersonAffectedDto,
        value: any
    ) => {
        setClaimUpdate(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                personAffected: {
                    ...prev.personAffected,
                    [field]: value
                }
            };
        });
    };

    const clearAffectedPersonData = () => {
        setClaimUpdate(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                personAffected: {
                    ...prev.personAffected,
                    nameAndSurname: "",
                    birthDate: null,
                    cip: "",
                    medicalHistoryNumber: "",
                    dni: "",
                    address: "",
                    city: "",
                    zipCode: "",
                    phone: "",
                    mobile: "",
                    email: ""
                }
            };
        });
    };


    const handleDateChange = (field: keyof ClaimFormDto, date: Date | null) => {
        if (date) {
            const utcDate = convertUtc(date);
            handleChange(field, utcDate?.getTime() ?? null);
        } else {
            handleChange(field, null);
        }
    };

    const handlePersonAffectedDateChange = (
        field: keyof ClaimPersonAffectedDto,
        date: Date | null
    ) => {
        setClaimUpdate(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                personAffected: {
                    ...prev.personAffected,
                    [field]: date ? convertUtc(date)?.getTime() ?? null : null
                }
            };
        });
    };

    const validateCopyData = () => {
        const p = claimUpdate?.personAffected;
        if (!!p?.nameAndSurname ||
            !!p?.cip ||
            !!p?.dni ||
            !!p?.address ||
            !!p?.city ||
            !!p?.zipCode ||
            !!p?.phone ||
            !!p?.mobile ||
            !!p?.email) {
            handleOpenCopyData();

        } else {
            handleCopyData();
        }

    }

    const handleCopyData = () => {
        const claimPersonAffectedDtoCopy: ClaimPersonAffectedDto = {
            nameAndSurname: claimUpdate.nameAndSurname ?? "",
            birthDate: claimUpdate.personAffected?.birthDate ?? null,
            cip: claimUpdate.cip ?? "",
            medicalHistoryNumber: claimUpdate.personAffected?.medicalHistoryNumber ?? "",
            dni: claimUpdate.dni ?? "",
            address: claimUpdate.address ?? "",
            city: claimUpdate.city ?? "",
            zipCode: claimUpdate.zipCode ?? "",
            phone: claimUpdate.phone ?? "",
            mobile: claimUpdate.mobile ?? "",
            email: claimUpdate.email ?? ""
        };
        setClaimUpdate(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                personAffected: claimPersonAffectedDtoCopy
            };
        });

        handleCloseCopyData();

    }

    const handleStatusClaim = (statusValue: number) => {

        if (Number(currentStatus.current) == 2 && statusValue != 2) {
            setStatus(statusValue);
            handleOpen();
        } else {
            if (statusValue == 2) {
                setIsMandatory(true);
                const utcDate = convertUtc(new Date());
                setClaimUpdate(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        status: statusValue,
                        closingDate: utcDate?.getTime() ?? null
                    };
                });
                return;
            }
            setClaimUpdate(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    status: statusValue,
                };
            })

        }
    }

    const handleProfessionalsChange = (
        professionals: ClaimProfesionalDto[]
    ) => {
        setClaimUpdate(prev => ({
            ...prev!,
            professionals
        }));
    };

    const handleCloseModal = () => {
        setClaimUpdate(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                status: status ?? prev.status,
                claimDeleteReasonId: null,
                closingDate: null
            };
        })
        handleClose();
    }

    const searchAffectedPerson = async () => {
        const medicalHistoryNumber = claimUpdate?.personAffected?.medicalHistoryNumber?.trim();
        if (isSearchingAffectedPerson) return;

        if (!medicalHistoryNumber) {
            setHasAffectedPersonSearchError(true);
            return;
        }

        setIsSearchingAffectedPerson(true);
        setHasAffectedPersonSearchError(false);
        try {
            const data = await getParticipantDataFromSoap(medicalHistoryNumber);
            const hasData = !!(
                data?.clinicalHistoryNumber ||
                data?.name ||
                data?.surname ||
                data?.cip ||
                data?.dni
            );

            if (!hasData) {
                clearAffectedPersonData();
                setHasAffectedPersonSearchError(true);
                setMessageError(t("affectedPersonNotFoundByMedicalHistory"));
                return;
            }

            handlePersonAffectedChange("medicalHistoryNumber", data.clinicalHistoryNumber);
            handlePersonAffectedChange("nameAndSurname", `${data.name} ${data.surname} ${data.surname2}`);
            if (data.birthDate) {
                const longBirthDate = new Date(data.birthDate).setHours(0, 0, 0, 0);
                handlePersonAffectedChange("birthDate", longBirthDate);
            }

            handlePersonAffectedChange("cip", data.cip);
            handlePersonAffectedChange("dni", data.dni);
            handlePersonAffectedChange("address", data.address);
            handlePersonAffectedChange("city", data.city);
            handlePersonAffectedChange("phone", data.phoneNumber);
            handlePersonAffectedChange("mobile", data.phoneNumber2);
            handlePersonAffectedChange("zipCode", data.zipCode);
            setHasAffectedPersonSearchError(false);
        } catch (err) {
            const fetchError = err as ApiErrorResponse;
            console.error(fetchError);
            clearAffectedPersonData();
            setHasAffectedPersonSearchError(true);
            setMessageError(fetchError?.message || t("affectedPersonNotFoundByMedicalHistory"));
        } finally {
            setIsSearchingAffectedPerson(false);
        }
    };

    const createFilter = (name: string, value?: string | null): SelectedFilter => ({
        name,
        description: "",
        values: value ? [value] : [],
    });


    const searchRelatedPeople = async (type: ClaimPersonTypeEnum) => {

        const filters: SelectedFilter[] = [
            createFilter(
                "Name",
                type === ClaimPersonTypeEnum.Affected
                    ? claimUpdate?.personAffected?.nameAndSurname
                    : claimUpdate?.nameAndSurname
            ),
            createFilter(
                "CIP",
                type === ClaimPersonTypeEnum.Affected
                    ? claimUpdate?.personAffected?.cip
                    : claimUpdate?.cip
            ),
            createFilter(
                "DNI",
                type === ClaimPersonTypeEnum.Affected
                    ? claimUpdate?.personAffected?.dni
                    : claimUpdate?.dni
            ),
            createFilter(
                "MedicalRecordNumber",
                type === ClaimPersonTypeEnum.Affected
                    ? claimUpdate?.personAffected?.medicalHistoryNumber
                    : undefined
            ),
            createFilter("ClaimId", claimUpdate?.id?.toString()),
        ];


        const table: FilterTableKey =
            type === ClaimPersonTypeEnum.Affected
                ? "claimsForPersonByAffectedPeople"
                : "claimsForPersonByClaimantClaims";

        dispatch(setSelectedFilters({
            table: table,
            filters: filters,
        }));
    }

    const showRelatedPeopleClaims = (event: React.MouseEvent, type: ClaimPersonTypeEnum) => {
        event.stopPropagation();
        if (type == ClaimPersonTypeEnum.Affected)
            setShowClaimsForAffectedPersonModal(true);
        else
            setShowClaimsForClaimantModal(true);
    }

    return (<>
        {claimsForAffectedPerson && claimsForAffectedPerson.items !=null && (
            <ClaimsForPersonModal
                show={showClaimsForAffectedPersonModal}
                claimsForPerson={claimsForAffectedPerson || []}
                onClose={() => setShowClaimsForAffectedPersonModal(false)}
                paginationType="claimsForPersonByAffectedPeople"
            />
        )}
        {claimsForClaimant && claimsForClaimant.items != null  && (
            <ClaimsForPersonModal
                show={showClaimsForClaimantModal}
                claimsForPerson={claimsForClaimant || []}
                onClose={() => setShowClaimsForClaimantModal(false)}
                paginationType="claimsForPersonByClaimantClaims"
            />
        )}
       
        <ModalGlobal
            show={showModal}
            onClose={handleClose}
            body={t("claim:modal.modalOpenClaimStatusTitle", { number: claimUpdate.id })}
            onSave={() => {
                handleCloseModal();
            }}
            saveText={t("common:buttons.buttonReopen")}
            closeText={t("common:buttons.buttonCancel")}
            isLoading={false}
        />
        <ModalGlobal
            show={showModalCopyData}
            onClose={handleCloseCopyData}
            body={t("claim:modal.modalCopyDataTitle")}
            onSave={() => {
                handleCopyData();
            }}
            saveText={t("common:buttons.buttonYes")}
            closeText={t("common:buttons.buttonNo")}
            isLoading={false}
        />
        <Row>
            <Col>
                <Row>
                    <Col xs={6} md={6}>
                        <Form.Label>{t("claim:newClaimInputType")}</Form.Label>
                        <Form.Select
                            className="form-control"
                            value={claimUpdate?.requestTypeId}
                            disabled={readOnly}
                            onChange={(e) => handleChange("requestTypeId", e.target.value)}
                        >
                            {claimRequestTypes?.map((claimRequestType) => (
                                <option key={claimRequestType.id} value={claimRequestType.id}>
                                    {claimRequestType.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col xs={3} md={3}>
                        <Form.Label>{t("claim:detail.labelUserRelated")}</Form.Label>
                        <Form.Select
                            className="form-control"
                            value={claimUpdate.idUserRelated ?? ""}
                            disabled={readOnly}
                            onChange={(e) =>
                                setClaimUpdate(prev => {
                                    if (!prev) return prev;
                                    return {
                                        ...prev,
                                        idUserRelated: e.target.value === "" ? null : Number(e.target.value),
                                    };
                                })
                            }
                        >
                            <option value=""></option>
                            {users?.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col xs={3} md={3}>
                        <Form.Group>
                            <Form.Label>{t("claim:detail.labelStatus")}</Form.Label>
                            <Form.Control
                                as="select"
                                className={`form-select`}
                                value={claimUpdate.status ?? ""}
                                onChange={(e) =>
                                    handleStatusClaim(Number(e.target.value))
                                }
                                disabled={isViewMode}
                            >
                                <option value="0">{t("claim:detail.claimStatusOpen")}</option>
                                <option value="1">{t("claim:detail.claimStatusInProcess")}</option>
                                <option value="2">{t("claim:detail.claimStatusClosed")}</option>
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col xs={3} md={3}>
                        <InputDatePicker
                            value={claimUpdate?.eventDate ? new Date(claimUpdate.eventDate) : null}
                            onChange={(date) => handleDateChange("eventDate", date)}
                            label={t("claim:newClaimInputEventDate")}
                            disabled={readOnly}
                        />
                    </Col>
                    <Col xs={3} md={3}>
                        <InputDatePicker
                            value={claimUpdate?.entryDate ? new Date(claimUpdate.entryDate) : null}
                            onChange={(date) => handleDateChange("entryDate", date)}
                            label={t("claim:newClaimInputOacEntryDate")}
                            disabled={readOnly}
                        />
                    </Col>
                    <Col xs={3} md={3}>
                        <InputDatePicker
                            value={claimUpdate?.createDate ? new Date(claimUpdate.createDate) : null}
                            onChange={(date) => handleDateChange("createDate", date)}
                            label={t("claim:newClaimInputCreateDate")}
                            disabled={true}
                        />
                    </Col>
                    <Col xs={3} md={3}>
                        <div className="w-100">
                            <label className="form-label">{t("claim:detail.daysToFinishTheDeadline")}</label>
                            <div className="info-input">
                                {claimUpdate.status == ClaimStatusEnum.Open || claimUpdate.status == ClaimStatusEnum.InProgress
                                    ? <>
                                        <span className="info-input-text"
                                            style={{
                                                backgroundColor: getDetailsTimelineBackgroundColor(claimUpdate.timelineStatus),
                                                color: claimUpdate.timelineStatus == TimelineStatusEnum.Out ? "white" : "black"
                                            }}
                                        >
                                            {t(`claim:timelineStatus.${TimelineStatusEnum[claimUpdate.timelineStatus]}`)}
                                        </span>
                                        {claimUpdate.timelineDays != null &&
                                            <span className="pl-1">
                                                {`${claimUpdate.timelineDays} d`}
                                            </span>
                                        }
                                    </>
                                    : <span className="info-input-text">
                                        -
                                    </span>
                                }
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="mt-4 collapsed-section">
                    <Row onClick={() => toggleSection("person")}>
                        <Col xs={8} md={8} className="d-flex">
                            <h5>{t("claim:headerDataPerson")}</h5>
                            {claimsForClaimant != null && claimsForClaimant.totalRecords > 0 && (
                                claimsForClaimant.hasClaimOpenOrInInProgress ? (
                                    <div
                                        className="ms-2 cursor-pointer"
                                        onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Claimant)}
                                    >
                                        <img
                                            title={t("claim:activeRequestsForPersonMessage")}
                                            alt={t("claim:activeRequestsForPersonMessage")}
                                            src={dangerTriangleIcon}
                                            width={30}
                                            height={30}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className="ms-2 cursor-pointer"
                                        onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Claimant)}
                                    >
                                        <img
                                            title={t("claim:requestsForPersonMessage")}
                                            alt={t("claim:requestsForPersonMessage")}
                                            src={successTriangleIcon}
                                            width={40}
                                            height={40}
                                        />
                                    </div>
                                )
                            )}
                        </Col>
                        <Col xs={4} md={4} className="text-end">
                            <i className={`collapsed-section-icon bi ${isCollapsed("person") ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </Col>
                    </Row>
                    {!isCollapsed("person") && <>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputNameAndSurname")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.nameAndSurname ?? ""}
                                        onChange={(e) => handleChange("nameAndSurname", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Label>{t("claim:newClaimInputRelationshipWithAffected")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.relationshipWithAffectedPersonId ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("relationshipWithAffectedPersonId", e.target.value)}>
                                    <option value=""></option>
                                    {claimPersonalRelationships?.map((claimPersonalRelationship) => (
                                        <option key={claimPersonalRelationship.id} value={claimPersonalRelationship.id}>
                                            {claimPersonalRelationship.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>CIP</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.cip ?? ""}
                                        onChange={(e) => handleChange("cip", e.target.value)}
                                    />

                                </Form.Group>
                            </Col>

                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputNameAndDni")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.dni ?? ""}
                                        onChange={(e) => handleChange("dni", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputAddress")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.address ?? ""}
                                        onChange={(e) => handleChange("address", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputZipCode")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.zipCode ?? ""}
                                        onChange={(e) => handleChange("zipCode", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputCity")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.city ?? ""}
                                        onChange={(e) => handleChange("city", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputEmail")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.email ?? ""}
                                        onChange={(e) => handleChange("email", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPhone")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.phone ?? ""}
                                        onChange={(e) => handleChange("phone", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputMobile")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.mobile ?? ""}
                                        onChange={(e) => handleChange("mobile", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>}
                </div>
                <div className="mt-4 collapsed-section">
                    <Row className="align-items-center" onClick={() => toggleSection("affectedPerson")}>
                        <Col xs={8} md={8} className="d-flex">
                            <h5>{t("claim:headerDataAffectedPerson")}</h5>
                            {claimsForAffectedPerson != null && claimsForAffectedPerson.totalRecords > 0 && (
                                claimsForAffectedPerson.hasClaimOpenOrInInProgress 
                                ? <div className="ms-2 cursor-pointer" onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Affected)}>
                                    <img
                                        title={t("claim:activeRequestsForPersonMessage")}
                                        alt={t("claim:activeRequestsForPersonMessage")}
                                        src={dangerTriangleIcon}
                                        width={30}
                                        height={30}
                                    />
                                </div>
                                : <div className="ms-2 cursor-pointer" onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Affected)}>
                                    <img
                                        title={t("claim:requestsForPersonMessage")}
                                        alt={t("claim:requestsForPersonMessage")}
                                        src={successTriangleIcon}
                                        width={40}
                                        height={40}
                                    />
                                </div>
                            )}
                        </Col>
                        <Col xs={4} md={4} className="text-end">
                            <i className={`collapsed-section-icon bi ${isCollapsed("affectedPerson") ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </Col>
                    </Row>
                    {!isCollapsed("affectedPerson") && <>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimMedicalHistoryNumber")}</Form.Label>
                                    <div className="input-calendar">
                                        <Form.Control
                                            type="text"
                                            readOnly={readOnly}
                                            value={claimUpdate?.personAffected?.medicalHistoryNumber ?? ""}
                                            onChange={(e) => {
                                                handlePersonAffectedChange("medicalHistoryNumber", e.target.value);
                                                if (hasAffectedPersonSearchError)
                                                    setHasAffectedPersonSearchError(false);
                                            }}
                                            isInvalid={hasAffectedPersonSearchError}
                                        />
                                        {hasAccessToClinicalHistory &&
                                            <span className="content-icon-calendar"
                                                onClick={searchAffectedPerson}
                                                style={{ cursor: isSearchingAffectedPerson ? "default" : "pointer" }}
                                            >
                                                {isSearchingAffectedPerson ? (
                                                    <Spinner animation="border" variant="secondary" size="sm" />
                                                ) : (
                                                    <img
                                                        style={{ marginRight: "5px", filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)" }}
                                                        title={t("common:toggles.searchByClinicHistoryNumber")}
                                                        src={notesMedicalIcon}
                                                        alt={t("common:toggles.searchByClinicHistoryNumber")}
                                                        width={25}
                                                        height={25}
                                                    />
                                                )}
                                            </span>
                                        }
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <InputDatePicker
                                    value={
                                        claimUpdate?.personAffected?.birthDate
                                            ? new Date(claimUpdate.personAffected.birthDate)
                                            : null
                                    }
                                    onChange={(date) =>
                                        handlePersonAffectedDateChange("birthDate", date)
                                    }
                                    label={t("claim:newClaimInputPersonAffectedBirthDate")}
                                    disabled={readOnly}
                                />
                            </Col>
                            {!isViewMode &&
                                <Col xs={6} md={6} className="mt-4 text-end">
                                    <Button variant="secondary" onClick={validateCopyData}>
                                        {t("claim:buttonCopyDades")}
                                    </Button>
                                </Col>
                            }
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputNameAndSurnameAffected")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.nameAndSurname ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("nameAndSurname", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>CIP</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.cip ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("cip", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputNameAndDni")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.dni ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("dni", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPersonAffectedAddress")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.address ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("address", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPersonAffectedZipCode")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.zipCode ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("zipCode", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPersonAffectedCity")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.city ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("city", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPersonAffectedEmail")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.email ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("email", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPersonAffectedPhone")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.phone ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("phone", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputPersonAffectedMobile")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.personAffected?.mobile ?? ""}
                                        onChange={(e) => handlePersonAffectedChange("mobile", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>}
                </div>
                <div className="mt-4 collapsed-section">
                    <Row className="align-items-center" onClick={() => toggleSection("reason")}>
                        <Col xs={8} md={8}>
                            <h5>{t("claim:headerDataReason")}</h5>
                        </Col>
                        <Col xs={4} md={4} className="text-end">
                            <i className={`collapsed-section-icon bi ${isCollapsed("reason") ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </Col>
                    </Row>
                    {!isCollapsed("reason") && <>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Label>{t("organizationalStructure:organizationalStructure")}*</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={
                                        claimUpdate?.organizationalStructureId
                                        ?? ""
                                    }
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("organizationalStructureId", parseInt(e.target.value))}
                                    isInvalid={!!errors.organizationalStructureId}
                                >
                                    <option value=""></option>
                                    {organizationalStructures?.map((organizationalStructure) => (
                                        <option key={organizationalStructure.id} value={organizationalStructure.id}>
                                            {'\u00A0'.repeat(organizationalStructure.level * 4)}{organizationalStructure.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.organizationalStructureId}
                                </Form.Control.Feedback>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Label>{t("claim:newClaimFunctionalArea")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.functionalAreaId ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("functionalAreaId", e.target.value)}
                                >
                                    <option value=""></option>
                                    {functionalAreas?.map((functionalArea) => (
                                        <option key={functionalArea.id} value={functionalArea.id}>
                                            {functionalArea.name}
                                        </option>
                                    ))}

                                </Form.Select>
                            </Col>
                            <Col xs={6} md={6} >
                                <Form.Label>{t("claim:newClaimServiceIndicator")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate.serviceIndicatorId?.toString() ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) =>
                                        setClaimUpdate(prev => {
                                            if (!prev) return prev;
                                            return {
                                                ...prev,
                                                serviceIndicatorId: e.target.value === "" ? 0 : Number(e.target.value),
                                            };
                                        })
                                    }>
                                    <option value=""></option>
                                    {serviceIndicators?.map((serviceIndicator) => (
                                        <option key={serviceIndicator.id} value={serviceIndicator.id}>
                                            {serviceIndicator.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            {/* No se debe mostrar el número de habitación segun el ticket http://gitsrv/Medidedalia/Qualia_Documentaci%C3%B3/_boards/board/t/Qualia_Documentaci%C3%B3%20Team/Stories/?workitem=2267 */}
                            {/* <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputRoomNumber")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.roomNumber ?? ""}
                                        onChange={(e) => handleChange("roomNumber", e.target.value)}
                                    />
                                </Form.Group>
                            </Col> */}
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Label>{t("claim:newClaimInputComment")}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    readOnly={readOnly}
                                    rows={5}
                                    value={claimUpdate?.comment ?? ""}
                                    onChange={(e) => handleChange("comment", e.target.value)}
                                />
                            </Col>

                        </Row>
                    </>}
                </div>
                <div className="mt-4 collapsed-section">
                    <Row className="align-items-center" onClick={() => toggleSection("professionals")}>
                        <Col xs={8} md={8}>
                            <h5>{t("claim:headerProfessionalData")}</h5>
                        </Col>
                        <Col xs={4} md={4} className="text-end">
                            <i className={`collapsed-section-icon bi ${isCollapsed("professionals") ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </Col>
                    </Row>
                    {!isCollapsed("professionals") &&
                        <ClaimProfessionalsSection
                            professionals={claimUpdate.professionals ?? []}
                            readOnly={readOnly}
                            serviceIndicators={serviceIndicators}
                            claimProfessionalTypes={claimProfessionalTypes}
                            onProfessionalsChange={handleProfessionalsChange}
                        />
                    }
                </div>
                <div className="mt-4 collapsed-section">
                    <Row className="align-items-center" onClick={() => toggleSection("internal")}>
                        <Col xs={8} md={8}>
                            <h5>{t("claim:headerInternalData")}</h5>
                        </Col>
                        <Col xs={4} md={4} className="text-end">
                            <i className={`collapsed-section-icon bi ${isCollapsed("internal") ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </Col>
                    </Row>
                    {!isCollapsed("internal") && <>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <InputDatePicker
                                        value={claimUpdate?.internalReportRequestDate ? new Date(claimUpdate?.internalReportRequestDate) : null}
                                        onChange={(date) => handleDateChange("internalReportRequestDate", date)}
                                        label={t("claim:newClaimInputInternalReportRequest")}
                                        disabled={readOnly}
                                    />

                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <InputDatePicker
                                    value={claimUpdate?.internalReportDate ? new Date(claimUpdate?.internalReportDate) : null}
                                    onChange={(date) => handleDateChange("internalReportDate", date)}
                                    label={t("claim:newClaimInputInternalReportDate")}
                                    disabled={readOnly}
                                />
                            </Col>
                            <Col xs={3} md={3}>
                                <InputDatePicker
                                    value={claimUpdate?.contactDate ? new Date(claimUpdate.contactDate) : null}
                                    onChange={(date) => handleDateChange("contactDate", date)}
                                    label={t("claim:contactDate")}
                                    disabled={readOnly}
                                />
                            </Col>
                            <Col xs={3} md={3}>
                                <InputDatePicker
                                    value={claimUpdate?.deliveryDate ? new Date(claimUpdate.deliveryDate) : null}
                                    onChange={(date) => handleDateChange("deliveryDate", date)}
                                    label={t("claim:newClaimInputDeliveyDate")}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputGREFile")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.expedientGRE ?? ""}
                                        onChange={(e) => handleChange("expedientGRE", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Label>{t("claim:newClaimInputShipmentStatusToGRE")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.shipmentStatus ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) =>
                                        handleChange("shipmentStatus", parseInt(e.target.value))
                                    }
                                >
                                    <option key={""}></option>
                                    {Object.entries(ClaimShipmentStatusEnum).map(([key, value]) => (
                                        <option key={value} value={value}>
                                            {t(`claim:newClaimInputShipmentStatusToGRE${key}`)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputRegisterNumber")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.registerNumber ?? ""}
                                        onChange={(e) => handleChange("registerNumber", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <InputDatePicker
                                    value={claimUpdate?.registerDate ? new Date(claimUpdate.registerDate) : null}
                                    onChange={(date) => handleDateChange("registerDate", date)}
                                    label={t("claim:newClaimInputRegisterDate")}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Label>{t("claim:newClaimInputFormat")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.claimInOutFormatInputId ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("claimInOutFormatInputId", e.target.value)}
                                >
                                    <option value=""></option>
                                    {[...(claimInOutFormats ?? [])]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Label>{t("claim:claimOrigins")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.originId ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("originId", e.target.value)}
                                >
                                    <option value=""></option>
                                    {claimOrigins?.map((origin) => (
                                        <option key={origin.id} value={origin.id}>
                                            {origin.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputOtherOrigin")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.otherOrigin ?? ""}
                                        onChange={(e) => handleChange("otherOrigin", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>}
                </div>
                <div className="mt-4 collapsed-section">
                    <Row className="align-items-center" onClick={() => toggleSection("closing")}>
                        <Col xs={8} md={8}>
                            <h5>{t("claim:headerClosingData")}</h5>
                        </Col>
                        <Col xs={4} md={4} className="text-end">
                            <i className={`collapsed-section-icon bi ${isCollapsed("closing") ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                        </Col>
                    </Row>
                    {!isCollapsed("closing") && <>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputOtherClosed")}</Form.Label>
                                    <Form.Select
                                        className="form-control"
                                        value={claimUpdate.claimDeleteReasonId ?? ""}
                                        disabled={readOnly && !isMandatory}
                                        onChange={(e) => handleChange("claimDeleteReasonId", e.target.value)}
                                        isInvalid={!!errors.claimDeleteReasonId}
                                    >
                                        <option value=""></option>
                                        {claimDeleteReasons?.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.claimDeleteReasonId}
                                    </Form.Control.Feedback>

                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <InputDatePicker
                                    value={claimUpdate?.closingDate ? new Date(claimUpdate.closingDate) : null}
                                    onChange={(date) => handleDateChange("closingDate", date)}
                                    label={t("claim:newClaimClosedData")}
                                    disabled={readOnly && !isMandatory}
                                />
                                {errors.closingDate && (
                                    <div className="invalid-feedback d-block">
                                        {errors.closingDate}
                                    </div>
                                )}
                            </Col>
                            <Col xs={3} md={3}>
                                <div className="w-100">
                                    <label className="form-label">{t("claim:detail.daysPassed")}</label>
                                    <div className="info-input">
                                        {claimUpdate.closingPassedDays != null ?
                                            <span className="info-input-text"
                                                style={{
                                                    backgroundColor: claimUpdate.closingPassedDaysAlert ? COLOR.VERMELL : "",
                                                    color: claimUpdate.closingPassedDaysAlert ? "white" : "black"
                                                }}
                                            >
                                                {`${claimUpdate.closingPassedDays} d`}
                                            </span> :
                                            <span className="info-input-text">
                                                -
                                            </span>
                                        }
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} md={6}>
                                <Form.Group>
                                    <Form.Label>{t("claim:newClaimInputResolution")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claimUpdate?.resolution ?? ""}
                                        onChange={(e) => handleChange("resolution", e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Label>{t("claim:newClaimInputReferraToAnotherManagementPoint")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.demandDerivationId ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("demandDerivationId", e.target.value === "" ? null : Number(e.target.value))}
                                >
                                    <option value=""></option>
                                    {demandDerivations?.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Label>{t("claim:newClaimInputOutputFormat")}</Form.Label>
                                <Form.Select
                                    className="form-control"
                                    value={claimUpdate?.claimInOutFormatOutputId ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => handleChange("claimInOutFormatOutputId", e.target.value)}
                                >
                                    <option value=""></option>
                                    {claimOutFormats?.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                    </>}
                </div>
            </Col>
        </Row>
    </>);
}
